/* ==========================================================================
   Dogs Supreme — Cart & Checkout Tracking
   Version: 1.0.0 (2026-04-15)

   Fires Custom-Events that the Google & YouTube App Pixel does not cover:
   remove_from_cart, cart_quantity_change, coupon_attempt, free_shipping_progress,
   shipping_cost_revealed, cart_drawer_interaction, checkout_cta_click,
   express_checkout_click, trust_signal_interaction.

   Rules:
   - Every push wrapped in try/catch (Tracking must never break Cart)
   - ecommerce: null reset before ecommerce events (GA4 hygiene)
   - Consent-gated via Google Consent Mode v2 (analytics_storage)
   - Fetch-Interceptor is the only source for cart-mutation events
   - /cart/add.js with header X-DS-Source: upsell|cross-sell|... is skipped
     so existing Upsell tracking is not duplicated
   ========================================================================== */

(function () {
  'use strict';

  if (window.__dsTrackingInstalled) return;
  window.__dsTrackingInstalled = true;

  var SHIP_THRESHOLD_CENTS = 7999; // 79,99 EUR = Checkout-Wahrheit (Experiment 4999 beendet 13.06.2026)
  var MARKED_SOURCES = ['upsell', 'cross-sell', 'cross-sell-reminder', 'system-engine'];
  var CART_ADD_RE = /\/cart\/add(\.js|\?|$)/;
  var CART_CHANGE_RE = /\/cart\/change(\.js|\?|$)/;
  var CART_UPDATE_RE = /\/cart\/update(\.js|\?|$)/;

  var state = { lastCart: null };

  function consentGranted() {
    try {
      var gtd = window.google_tag_data;
      var ent = gtd && gtd.ics && gtd.ics.entries;
      if (!ent || !ent.analytics_storage) return false;
      var a = ent.analytics_storage;
      // Google Consent Mode v2 liefert Boolean (true/false) seit 2024, nicht String ('granted')
      var updateGranted = a.update === 'granted' || a.update === true;
      var defaultGranted = a.default === 'granted' || a.default === true;
      return updateGranted || (a.update === undefined && defaultGranted);
    } catch (e) { return false; }
  }

  function pushEvent(name, params) {
    if (!consentGranted()) return;
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      if (params) for (var k in params) if (params.hasOwnProperty(k)) payload[k] = params[k];
      window.dataLayer.push(payload);
    } catch (e) { console.warn('[DS-Tracking] pushEvent:', name, e); }
  }

  function pushEcommerce(name, ecommerce) {
    if (!consentGranted()) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: name, ecommerce: ecommerce });
    } catch (e) { console.warn('[DS-Tracking] pushEcommerce:', name, e); }
  }

  function clarityEvent(name) {
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  }

  function claritySet(key, value) {
    try { if (typeof window.clarity === 'function') window.clarity('set', key, String(value)); } catch (e) {}
  }

  function lineToGa4Item(line) {
    return {
      item_id: String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_brand: line.vendor || '',
      price: Number(((line.final_price != null ? line.final_price : line.price) || 0) / 100),
      quantity: line.quantity || 0
    };
  }

  function updateClarityCartTags(cart) {
    if (!cart) return;
    claritySet('cart_value', (cart.total_price / 100).toFixed(2));
    claritySet('cart_items_count', cart.item_count);
    claritySet('free_shipping_qualified', cart.total_price >= SHIP_THRESHOLD_CENTS ? 'yes' : 'no');
  }

  function emitFreeShippingProgress(cart) {
    if (!cart) return;
    var qualified = cart.total_price >= SHIP_THRESHOLD_CENTS;
    pushEvent('free_shipping_progress', {
      cart_value: cart.total_price,
      threshold: SHIP_THRESHOLD_CENTS,
      amount_remaining: Math.max(0, SHIP_THRESHOLD_CENTS - cart.total_price),
      qualifies: qualified
    });
    clarityEvent(qualified ? 'free_shipping_qualified' : 'free_shipping_not_qualified');
  }

  function readSourceHeader(init, input) {
    try {
      var headers = (init && init.headers) || (input && input.headers) || null;
      if (!headers) return null;
      if (typeof headers.get === 'function') return headers.get('X-DS-Source');
      return headers['X-DS-Source'] || headers['x-ds-source'] || null;
    } catch (e) { return null; }
  }

  function diffAndEmitCartChanges(oldCart, newCart) {
    if (!oldCart || !oldCart.items) return;
    var oldMap = {};
    for (var i = 0; i < oldCart.items.length; i++) oldMap[oldCart.items[i].key] = oldCart.items[i];
    var newMap = {};
    for (var j = 0; j < newCart.items.length; j++) newMap[newCart.items[j].key] = newCart.items[j];

    for (var k in oldMap) {
      if (!newMap[k]) {
        var removed = oldMap[k];
        pushEcommerce('remove_from_cart', {
          currency: 'EUR',
          value: Number(((removed.final_line_price != null ? removed.final_line_price : removed.line_price) || 0) / 100),
          items: [lineToGa4Item(removed)]
        });
        clarityEvent('remove_from_cart');
      }
    }
    for (var k2 in newMap) {
      if (oldMap[k2] && oldMap[k2].quantity !== newMap[k2].quantity) {
        var it = newMap[k2];
        var oldQ = oldMap[k2].quantity;
        var newQ = it.quantity;
        pushEvent('cart_quantity_change', {
          item_id: String(it.variant_id || it.id),
          item_name: it.product_title || it.title,
          old_quantity: oldQ,
          new_quantity: newQ,
          direction: newQ > oldQ ? 'increase' : 'decrease',
          cart_value: newCart.total_price / 100
        });
        clarityEvent('quantity_change');
      }
    }
  }

  function refreshCart() {
    if (!window.fetch) return Promise.resolve(null);
    return window.fetch('/cart.js', { credentials: 'same-origin', headers: { 'X-DS-Source': 'ds-tracking-state' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) { state.lastCart = c; updateClarityCartTags(c); } return c; })
      .catch(function () { return null; });
  }

  function installFetchInterceptor() {
    if (!window.fetch || window.__dsTrackingFetchInstalled) return;
    window.__dsTrackingFetchInstalled = true;
    var origFetch = window.fetch.bind(window);

    /* FIX #6 Phase 2: Hydratisiere preCart wenn null vor Change/Update.
       Verhindert silent loss vom ersten Mutation-Call nach Page-Load.
       Verwendet origFetch (umgeht patched fetch) → keine Endlosschleife.
       Async, daher User-Click wartet ~100-300ms auf erste Hydration.
       Ab 2. Call: state.lastCart hydratiert → kein Delay. */
    function hydratePreCartIfMissing() {
      if (state.lastCart) return Promise.resolve(state.lastCart);
      return origFetch('/cart.js', { credentials: 'same-origin', headers: { 'X-DS-Source': 'ds-tracking-hydrate' } })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (c) {
          if (c) {
            state.lastCart = c;
            console.warn('[DS-Tracking] preCart hydrated lazily', { items: c.items && c.items.length, total: c.total_price });
          }
          return c;
        })
        .catch(function (err) {
          console.error('[DS-Tracking] preCart hydration failed', err);
          return null;
        });
    }

    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var isAdd = CART_ADD_RE.test(url);
      var isChange = CART_CHANGE_RE.test(url);
      var isUpdate = CART_UPDATE_RE.test(url);
      var source = (isAdd || isChange || isUpdate) ? readSourceHeader(init, input) : null;

      /* Phase 2: Bei Change/Update mit null preCart ZUERST hydratisieren, DANN echten Call.
         Add-Calls brauchen kein preCart (response = neue Line). */
      if ((isChange || isUpdate) && !state.lastCart) {
        return hydratePreCartIfMissing().then(function () {
          return executeMutation(input, init, url, isAdd, isChange, isUpdate, source);
        });
      }

      return executeMutation(input, init, url, isAdd, isChange, isUpdate, source);
    };

    function executeMutation(input, init, url, isAdd, isChange, isUpdate, source) {
      var preCart = state.lastCart;
      var promise = origFetch(input, init);

      if (!(isAdd || isChange || isUpdate)) return promise;

      return promise.then(function (resp) {
        if (!resp || !resp.ok) {
          if (resp) console.warn('[DS-Tracking] cart fetch non-OK', { url: url, status: resp.status });
          return resp;
        }
        try {
          var clone = resp.clone();
          clone.json().then(function (data) {
            try {
              handleCartMutation({ isAdd: isAdd, isChange: isChange, isUpdate: isUpdate, source: source, preCart: preCart, response: data });
            } catch (err) {
              /* FIX #6 Loud-Catch: handler-Errors waren silent — Event verloren ohne Spur */
              console.error('[DS-Tracking] mutation handler exception', err, { url: url, hasPreCart: !!preCart, source: source });
            }
          }).catch(function (err) {
            /* FIX #6 Loud-Catch: JSON-Parse-Fail (Redirect, HTML-Error-Page, malformed) → vorher silent */
            console.error('[DS-Tracking] cart response json parse failed', err, { url: url });
          });
        } catch (e) {
          console.error('[DS-Tracking] fetch intercept exception', e, { url: url });
        }
        return resp;
      });
    };
  }

  function handleCartMutation(ctx) {
    if (ctx.isAdd) {
      var line = ctx.response;
      var unmarked = MARKED_SOURCES.indexOf(ctx.source) === -1;
      if (line && unmarked) {
        pushEcommerce('add_to_cart', {
          currency: 'EUR',
          value: Number(((line.final_line_price != null ? line.final_line_price : line.line_price) || line.price || 0) / 100),
          items: [lineToGa4Item(line)]
        });
        clarityEvent('add_to_cart');
      }
      refreshCart().then(function (c) { if (c) emitFreeShippingProgress(c); });
    } else if (ctx.isChange || ctx.isUpdate) {
      var newCart = ctx.response;
      if (!newCart || !newCart.items) return;
      diffAndEmitCartChanges(ctx.preCart, newCart);
      state.lastCart = newCart;
      updateClarityCartTags(newCart);
      emitFreeShippingProgress(newCart);
    }
  }

  function cartSummary() {
    var c = state.lastCart;
    return {
      cart_value: c ? c.total_price / 100 : 0,
      cart_items_count: c ? c.item_count : 0
    };
  }

  function installClickDelegation() {
    document.addEventListener('click', function (e) {
      try {
        var marked = e.target.closest('[data-ds-track]');
        if (marked) {
          var kind = marked.getAttribute('data-ds-track');
          var loc = marked.getAttribute('data-ds-location') || null;
          var method = marked.getAttribute('data-method') || null;
          var signal = marked.getAttribute('data-signal-type') || null;
          var trigger = marked.getAttribute('data-trigger') || null;
          var s = cartSummary();
          switch (kind) {
            case 'checkout-cta':
              pushEvent('checkout_cta_click', { location: loc || 'cart_page', cart_value: s.cart_value, cart_items_count: s.cart_items_count });
              clarityEvent('checkout_cta_click');
              return;
            case 'express-checkout':
              pushEvent('express_checkout_click', { method: method || 'unknown', location: loc || 'cart_page', cart_value: s.cart_value });
              clarityEvent('express_checkout_click');
              return;
            case 'cart-drawer-open':
              pushEvent('cart_drawer_interaction', { action: 'open', trigger: trigger || 'unknown', cart_items_count: s.cart_items_count, cart_value: s.cart_value });
              clarityEvent('cart_drawer_open');
              return;
            case 'cart-drawer-close':
              pushEvent('cart_drawer_interaction', { action: 'close', trigger: trigger || 'unknown', cart_items_count: s.cart_items_count, cart_value: s.cart_value });
              clarityEvent('cart_drawer_close');
              return;
            case 'trust-signal':
              pushEvent('trust_signal_interaction', { signal_type: signal || 'unknown', action: 'click', location: loc || 'pdp' });
              clarityEvent('trust_signal_click');
              return;
          }
        }

        /* Fallback-Selektoren fuer Theme-Elemente ohne data-ds-track.
           Fragil bei Theme-Updates — bei Renamed Klassen hier anpassen. */
        var checkoutBtn = e.target.closest(
          '[href="/checkout"], [name="checkout"], button[name="checkout"], .cart__checkout, .drawer__checkout, #CartDrawer-Checkout, .cart-drawer__checkout'
        );
        if (checkoutBtn) {
          var isDrawer = /drawer|CartDrawer/i.test(checkoutBtn.className + ' ' + (checkoutBtn.id || ''));
          var su = cartSummary();
          pushEvent('checkout_cta_click', {
            location: isDrawer ? 'cart_drawer' : 'cart_page',
            cart_value: su.cart_value,
            cart_items_count: su.cart_items_count
          });
          clarityEvent('checkout_cta_click');
          return;
        }

        var express = e.target.closest(
          'shop-pay-button, [data-shopify-buttoncontainer] button, .shopify-payment-button, [class*="dynamic-checkout"]'
        );
        if (express) {
          var method = 'shop_pay';
          if (/paypal/i.test(express.outerHTML)) method = 'paypal_express';
          else if (/apple/i.test(express.outerHTML)) method = 'apple_pay';
          else if (/google/i.test(express.outerHTML)) method = 'google_pay';
          var se = cartSummary();
          pushEvent('express_checkout_click', { method: method, location: 'pdp_or_cart', cart_value: se.cart_value });
          clarityEvent('express_checkout_click');
        }
      } catch (err) { console.warn('[DS-Tracking] click:', err); }
    }, { passive: true, capture: true });
  }

  function installCouponListener() {
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        if (!form || !form.getAttribute) return;
        var action = form.getAttribute('action') || '';
        if (!/discount|\/cart/.test(action)) return;
        var input = form.querySelector('input[name="discount"], input[name="checkout[reduction_code]"]');
        var code = input && input.value ? input.value.trim() : '';
        if (!code) return;
        var s = cartSummary();
        pushEvent('coupon_attempt', { coupon_code: code, result: 'submitted', cart_value: s.cart_value });
        clarityEvent('coupon_attempt');
      } catch (err) { console.warn('[DS-Tracking] coupon:', err); }
    }, { passive: true });
  }

  /* =======================================================================
     Silent-Abandoner IdleTracker — tracks idle time, tab switches,
     scroll depth, and exit-without-checkout in the cart area.
     ======================================================================= */

  function installIdleTracker() {
    var IDLE_THRESHOLDS = [15, 30, 60];
    var SCROLL_THRESHOLDS = [25, 50, 75, 100];
    var TAB_MIN_HIDDEN_S = 5;

    var idle = {
      active: false,
      cartPath: null,
      cartOpenTime: 0,
      lastActivity: 0,
      firedIdle: {},
      firedScroll: {},
      tabSwitches: 0,
      tabHiddenTime: 0,
      tabHidden: false,
      maxIdleSeconds: 0,
      maxScrollDepth: 0,
      checkoutClicked: false,
      exitFired: false,
      intervalId: null,
      lastMouseMove: 0,
      scrollTimeout: null
    };

    function resetState() {
      idle.firedIdle = {};
      idle.firedScroll = {};
      idle.tabSwitches = 0;
      idle.tabHiddenTime = 0;
      idle.tabHidden = false;
      idle.maxIdleSeconds = 0;
      idle.maxScrollDepth = 0;
      idle.checkoutClicked = false;
      idle.exitFired = false;
      for (var i = 0; i < IDLE_THRESHOLDS.length; i++) idle.firedIdle[IDLE_THRESHOLDS[i]] = false;
      for (var j = 0; j < SCROLL_THRESHOLDS.length; j++) idle.firedScroll[SCROLL_THRESHOLDS[j]] = false;
    }

    function commonParams() {
      var s = cartSummary();
      return {
        cart_value: s.cart_value,
        cart_items_count: s.cart_items_count,
        cart_path: idle.cartPath || 'unknown',
        device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
      };
    }

    function start(path) {
      if (idle.active) return;
      idle.active = true;
      idle.cartPath = path;
      idle.cartOpenTime = Date.now();
      idle.lastActivity = Date.now();
      resetState();
      idle.intervalId = setInterval(tick, 1000);
    }

    function stop() {
      if (!idle.active) return;
      if (!idle.checkoutClicked && !idle.exitFired) {
        fireExit();
      }
      idle.active = false;
      if (idle.intervalId) { clearInterval(idle.intervalId); idle.intervalId = null; }
    }

    function resetTimer() {
      if (!idle.active) return;
      idle.lastActivity = Date.now();
    }

    function tick() {
      if (!idle.active || idle.tabHidden) return;
      var elapsed = Math.floor((Date.now() - idle.lastActivity) / 1000);
      if (elapsed > idle.maxIdleSeconds) idle.maxIdleSeconds = elapsed;
      for (var i = 0; i < IDLE_THRESHOLDS.length; i++) {
        var t = IDLE_THRESHOLDS[i];
        if (elapsed >= t && !idle.firedIdle[t]) {
          idle.firedIdle[t] = true;
          var p = commonParams();
          p.idle_seconds = t;
          pushEvent('cart_idle_' + t + 's', p);
          clarityEvent('cart_idle_' + t + 's');
        }
      }
    }

    function onVisibilityChange() {
      if (!idle.active) return;
      try {
        if (document.hidden) {
          idle.tabHidden = true;
          idle.tabHiddenTime = Date.now();
          pushEvent('cart_tab_hidden', commonParams());
          clarityEvent('cart_tab_hidden');
        } else {
          if (idle.tabHidden && idle.tabHiddenTime > 0) {
            var hiddenSec = Math.floor((Date.now() - idle.tabHiddenTime) / 1000);
            if (hiddenSec >= TAB_MIN_HIDDEN_S) {
              idle.tabSwitches++;
              var p = commonParams();
              p.hidden_duration_seconds = hiddenSec;
              pushEvent('cart_tab_returned', p);
              clarityEvent('cart_tab_returned');
            }
          }
          idle.tabHidden = false;
          idle.lastActivity = Date.now();
        }
      } catch (e) { console.warn('[DS-Tracking] visibility:', e); }
    }

    function onScroll() {
      if (!idle.active) return;
      resetTimer();
      if (idle.scrollTimeout) return;
      idle.scrollTimeout = setTimeout(function () {
        idle.scrollTimeout = null;
        try {
          var container, scrollTop, scrollHeight, clientHeight;
          if (idle.cartPath === 'drawer') {
            container = document.querySelector('.drawer__scrollable');
            if (!container) return;
            scrollTop = container.scrollTop;
            scrollHeight = container.scrollHeight;
            clientHeight = container.clientHeight;
          } else {
            scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            scrollHeight = document.documentElement.scrollHeight;
            clientHeight = window.innerHeight;
          }
          if (scrollHeight <= clientHeight) return;
          var pct = Math.round(scrollTop / (scrollHeight - clientHeight) * 100);
          if (pct > idle.maxScrollDepth) idle.maxScrollDepth = pct;
          for (var i = 0; i < SCROLL_THRESHOLDS.length; i++) {
            var t = SCROLL_THRESHOLDS[i];
            if (pct >= t && !idle.firedScroll[t]) {
              idle.firedScroll[t] = true;
              var p = commonParams();
              p.depth_percent = t;
              pushEvent('cart_scroll_depth', p);
              if (t === 100) clarityEvent('cart_scroll_complete');
            }
          }
        } catch (e) { console.warn('[DS-Tracking] scroll:', e); }
      }, 200);
    }

    function fireExit() {
      if (idle.exitFired || idle.checkoutClicked) return;
      idle.exitFired = true;
      try {
        var dwellSec = Math.floor((Date.now() - idle.cartOpenTime) / 1000);
        if (dwellSec < 2) return;
        var p = commonParams();
        p.time_in_cart_seconds = dwellSec;
        p.max_idle_seconds = idle.maxIdleSeconds;
        p.tab_switches = idle.tabSwitches;
        p.scroll_depth_percent = idle.maxScrollDepth;
        pushEvent('cart_exit_without_checkout', p);
        clarityEvent('cart_exit_no_checkout');
        claritySet('cart_dwell_seconds', dwellSec);
        claritySet('max_idle_seconds', idle.maxIdleSeconds);
        claritySet('cart_scroll_depth', idle.maxScrollDepth);
        claritySet('cart_tab_switches', idle.tabSwitches);
      } catch (e) { console.warn('[DS-Tracking] exit:', e); }
    }

    /* Interaction listeners (throttled/passive) */
    var onMouseMove = function () {
      var now = Date.now();
      if (now - idle.lastMouseMove < 1000) return;
      idle.lastMouseMove = now;
      resetTimer();
    };
    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('click', resetTimer, { passive: true });
    document.addEventListener('touchstart', resetTimer, { passive: true });
    document.addEventListener('keydown', resetTimer, { passive: true });

    /* Scroll — on window (cart-page) + drawer container (via MutationObserver) */
    window.addEventListener('scroll', onScroll, { passive: true });
    var drawerEl = document.querySelector('.drawer__scrollable');
    if (drawerEl) drawerEl.addEventListener('scroll', onScroll, { passive: true });

    /* Visibility */
    document.addEventListener('visibilitychange', onVisibilityChange);

    /* Drawer events */
    document.addEventListener('drawerOpen', function () { start('drawer'); });
    document.addEventListener('drawerOpen.CartDrawer', function () { start('drawer'); });
    document.addEventListener('cart:close', function () { stop(); });

    /* Cart page auto-start */
    if (document.body.classList.contains('template-cart')) {
      start('page');
    }

    /* Exit detection */
    window.addEventListener('pagehide', function () {
      if (idle.active) fireExit();
    });

    /* Checkout click suppression — listen for our existing checkout_cta_click */
    document.addEventListener('click', function (e) {
      try {
        var btn = e.target.closest('[href="/checkout"], [name="checkout"], .cart__checkout, #CartDrawer-Checkout, [data-ds-track="checkout-cta"]');
        if (btn) idle.checkoutClicked = true;
      } catch (err) {}
    }, { passive: true, capture: true });
  }

  function installUspRenderedTracker() {
    if (!consentGranted()) return;

    function fireEvent() {
      try {
        var uspBlock = document.querySelector('.ds-cart-usp');
        if (!uspBlock || uspBlock.dataset.uspFired === 'true') return;
        uspBlock.dataset.uspFired = 'true';

        var uspCount = parseInt(uspBlock.getAttribute('data-cart-usp-count'), 10) || 0;
        var items = uspBlock.querySelectorAll('.ds-cart-usp__item');
        var categories = [];
        for (var i = 0; i < items.length; i++) {
          categories.push(items[i].getAttribute('data-usp-category') || 'unknown');
        }

        var hasLineItemUsps = document.querySelectorAll('.ds-line-item-usp').length > 0;

        pushEvent('cart_usp_rendered', {
          usp_count: uspCount,
          usp_categories: categories.join(','),
          has_line_item_usps: hasLineItemUsps,
          cart_value: state.lastCart ? state.lastCart.total_price / 100 : null,
          cart_items_count: state.lastCart ? state.lastCart.item_count : null
        });
      } catch (e) {}
    }

    /* Cart page: fire immediately on init */
    if (document.body.classList.contains('template-cart')) {
      fireEvent();
    }

    /* Cart drawer: fire on open */
    document.addEventListener('drawerOpen', fireEvent);
    document.addEventListener('drawerOpen.CartDrawer', fireEvent);
  }

  /* =======================================================================
     Cart-Drawer Lifecycle: open via drawerOpen-Event, close via MutationObserver
     auf .cart-drawer--open Klassen-Transition. Theme dispatched cart:close nie,
     darum brauchen wir DOM-State-Tracking. Idempotent-Sentinel verhindert
     Doppelung mit installClickDelegation data-ds-track Path.
     ======================================================================= */
  function installCartDrawerLifecycle() {
    var drawerState = { open: false };

    function pushOpen(trigger) {
      if (drawerState.open) return;
      drawerState.open = true;
      var s = cartSummary();
      pushEvent('cart_drawer_interaction', {
        action: 'open', trigger: trigger || 'drawer_event',
        cart_items_count: s.cart_items_count, cart_value: s.cart_value
      });
      clarityEvent('cart_drawer_open');
    }
    function pushClose(trigger) {
      if (!drawerState.open) return;
      drawerState.open = false;
      var s = cartSummary();
      pushEvent('cart_drawer_interaction', {
        action: 'close', trigger: trigger || 'mutation_observer',
        cart_items_count: s.cart_items_count, cart_value: s.cart_value
      });
      clarityEvent('cart_drawer_close');
    }

    /* Pfad A: body.sn-cart-open Klassen-Transition (verifiziert 2026-04-28
       als der ECHTE Open/Close-Marker des Themes). drawerOpen Custom-Event
       wird vom aktuellen Theme NICHT dispatched. */
    function watchBodyClass() {
      if (document.body.classList.contains('sn-cart-open')) pushOpen('body_class_initial');
      var bodyObs = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (records[i].attributeName !== 'class') continue;
          var isOpen = document.body.classList.contains('sn-cart-open');
          if (isOpen && !drawerState.open) pushOpen('body_class_added');
          else if (!isOpen && drawerState.open) pushClose('body_class_removed');
        }
      });
      bodyObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    if (document.body) watchBodyClass();
    else document.addEventListener('DOMContentLoaded', watchBodyClass);

    /* Pfad B Backwards-compat: drawerOpen / cart:close falls Theme sie doch dispatched */
    document.addEventListener('drawerOpen', function (e) {
      var isCart = e && e.detail && (e.detail.id === 'CartDrawer' || /cart/i.test(e.detail.id || ''));
      if (isCart) pushOpen('drawer_open_event');
    });
    document.addEventListener('drawerOpen.CartDrawer', function () { pushOpen('drawer_open_namespaced'); });
    document.addEventListener('cart:close', function () { pushClose('cart_close_event'); });
  }

  function init() {
    /* Fetch-Interceptor MUST install immediately (before any cart action) */
    installFetchInterceptor();
    refreshCart();
    /* Defer click/coupon/idle/usp listeners to idle to protect LCP/TBT */
    var defer = window.requestIdleCallback || function (cb) { setTimeout(cb, 1000); };
    defer(function () { installClickDelegation(); installCouponListener(); installIdleTracker(); installUspRenderedTracker(); installCartDrawerLifecycle(); });
  }

  window.DSTracking = {
    state: state,
    pushEvent: pushEvent,
    pushEcommerce: pushEcommerce,
    clarityEvent: clarityEvent,
    claritySet: claritySet,
    refreshCart: refreshCart,
    consentGranted: consentGranted
  };

  if (document.readyState === 'complete' || document.readyState === 'interactive') init();
  else document.addEventListener('DOMContentLoaded', init);
})();

/* ════════════════════════════════════════════════════════════════════════
 * Dogs Supreme — Conviction-Tracking (PDP-Pilot, 2026-05-11)
 * IntersectionObserver auf .ds-bestseller-badge + .ds-reassurance
 * Feuert `ds_conviction_view` Event sobald Komponente >= 50% sichtbar.
 * ════════════════════════════════════════════════════════════════════════ */
(function() {
  'use strict';
  if (!('IntersectionObserver' in window)) return;

  function setupConvictionTracking() {
    var components = [
      { selector: '.ds-bestseller-badge', name: 'bestseller_badge' },
      { selector: '.ds-reassurance',      name: 'reassurance_section' }
    ];
    components.forEach(function(comp) {
      var el = document.querySelector(comp.selector);
      if (!el) return;
      var fired = false;
      new IntersectionObserver(function(entries) {
        if (fired) return;
        var entry = entries[0];
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        fired = true;
        window.dataLayer = window.dataLayer || [];
        var payload = {
          event: 'ds_conviction_view',
          component: comp.name,
          product_handle: el.getAttribute('data-product-handle') || 'unknown',
          experiment_group: document.body.getAttribute('data-exp-pdp-conviction') || null
        };
        if (comp.name === 'bestseller_badge') {
          var countEl = el.querySelector('.ds-bestseller-badge__count');
          payload.view_count_shown = countEl
            ? (parseInt(countEl.getAttribute('data-count'), 10) || 0)
            : 0;
        }
        window.dataLayer.push(payload);
      }, { threshold: [0.5] }).observe(el);
    });

    // Pop-In-on-Scroll fuer [data-ds-popin] Elements (Reassurance + Quote)
    var popins = document.querySelectorAll('[data-ds-popin]');
    popins.forEach(function(el) {
      new IntersectionObserver(function(entries, observer) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
            el.classList.add('is-visible');
            observer.unobserve(el);
          }
        });
      }, { threshold: [0.15, 0.5] }).observe(el);
    });
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setupConvictionTracking();
  } else {
    document.addEventListener('DOMContentLoaded', setupConvictionTracking);
  }
})();
